/**
 * MarketingLeadsPanel — Operations Console > Growth / Demo Requests.
 *
 * Platform-admin-only surface. Lists incoming Request-Demo leads from the
 * public marketing site, allows status updates, assignment, next-action
 * scheduling, and internal note threads.
 *
 * Backend endpoints (all behind require_platform_admin):
 *   GET   /api/marketing/demo-requests
 *   PATCH /api/marketing/demo-requests/{id}
 *   POST  /api/marketing/demo-requests/{id}/notes
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  Loader2, RefreshCw, MailOpen, ClipboardList, Search,
  CheckCircle2, X, Send, UserCheck, ArrowRight, Copy,
  ExternalLink, Building2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  new:                 { bg: "rgba(56,189,248,0.12)",  border: "rgba(56,189,248,0.45)",  text: "#7dd3fc" },
  contacted:           { bg: "rgba(245,158,11,0.10)",  border: "rgba(245,158,11,0.40)",  text: "#fbbf24" },
  discovery_scheduled: { bg: "rgba(245,158,11,0.10)",  border: "rgba(245,158,11,0.40)",  text: "#fbbf24" },
  demo_scheduled:      { bg: "rgba(249,115,22,0.12)",  border: "rgba(249,115,22,0.45)",  text: "#fb923c" },
  proposal_sent:       { bg: "rgba(168,85,247,0.10)",  border: "rgba(168,85,247,0.40)",  text: "#c4b5fd" },
  converted:           { bg: "rgba(249,115,22,0.18)",  border: "rgba(249,115,22,0.65)",  text: "#fb923c" },
  won:                 { bg: "rgba(34,197,94,0.12)",   border: "rgba(34,197,94,0.45)",   text: "#86efac" },
  lost:                { bg: "rgba(220,38,38,0.10)",   border: "rgba(220,38,38,0.40)",   text: "#fca5a5" },
  not_fit:             { bg: "rgba(107,114,128,0.12)", border: "rgba(107,114,128,0.45)", text: "#cbd5e1" },
  archived:            { bg: "rgba(107,114,128,0.10)", border: "rgba(107,114,128,0.35)", text: "#9ca3af" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return "—"; }
};

const COMPANY_TYPE_LABELS = {
  heavy_civil:      "Heavy Civil",
  dot_highway:      "DOT / Highway",
  airport_faa:      "Airport / FAA",
  utilities:        "Utilities",
  site_development: "Site Development",
  drainage:         "Drainage",
  municipal:        "Municipal Infrastructure",
  industrial:       "Industrial Construction",
  engineering:      "Engineering / Design",
  owner:            "Owner / Agency",
  other:            "Other",
};

export default function MarketingLeadsPanel() {
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState(null);
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (filter) params.q = filter;
      const { data } = await api.get("/marketing/demo-requests", { params });
      setLeads(data.leads || []);
      setCounts(data.counts || null);
      setStatuses(data.statuses || {});
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [filter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div
      className="rounded-xl border bg-[var(--fop-surface)]"
      style={{ borderColor: "var(--fop-border)" }}
      data-testid="marketing-leads-panel"
    >
      <div className="px-5 py-4 border-b flex items-center justify-between gap-3 flex-wrap"
           style={{ borderColor: "var(--fop-border)" }}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md inline-flex items-center justify-center"
               style={{
                 background: "rgba(249, 115, 22, 0.10)",
                 border: "1px solid rgba(249, 115, 22, 0.35)",
               }}>
            <MailOpen size={16} className="text-[var(--fop-primary)]" />
          </div>
          <div>
            <div className="text-[var(--fop-text)] font-semibold">Demo Requests</div>
            <div className="text-xs text-[var(--fop-text-3)] font-mono uppercase tracking-wider">
              Marketing leads · platform admin only
            </div>
          </div>
        </div>
        <button
          onClick={load}
          className="btn btn-ghost"
          style={{ minHeight: "36px", padding: "0 12px" }}
          data-testid="leads-refresh"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {counts && (
        <div className="px-5 py-3 border-b flex flex-wrap gap-2 text-[11px] font-mono uppercase tracking-wider"
             style={{ borderColor: "var(--fop-border)" }}>
          <CountChip label="Total"     value={counts.total ?? 0} active={statusFilter === ""} onClick={() => setStatusFilter("")} testid="count-total" />
          <CountChip label="Open"      value={counts.open ?? 0}  active={false} onClick={() => setStatusFilter("")} />
          {Object.entries(statuses).map(([code, label]) => (
            <CountChip
              key={code}
              label={label}
              value={counts[code] ?? 0}
              status={code}
              active={statusFilter === code}
              onClick={() => setStatusFilter(statusFilter === code ? "" : code)}
              testid={`count-${code}`}
            />
          ))}
        </div>
      )}

      <div className="px-5 py-3 border-b" style={{ borderColor: "var(--fop-border)" }}>
        <div className="relative max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fop-text-3)]" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search company, name, email, message…"
            className="w-full h-10 pl-9 pr-3 rounded-md text-sm"
            data-testid="leads-search"
          />
        </div>
      </div>

      <div>
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[var(--fop-text-3)]" /></div>
        ) : leads.length === 0 ? (
          <div className="p-12 text-center text-[var(--fop-text-3)] text-sm" data-testid="leads-empty">
            <ClipboardList className="w-7 h-7 text-[var(--fop-text-4)] mx-auto mb-3" />
            No demo requests {statusFilter || filter ? "match the current filter" : "yet"}.
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--fop-border)" }}>
            {leads.map((l) => (
              <LeadRow key={l.id} lead={l} onOpen={() => setActive(l)} />
            ))}
          </div>
        )}
      </div>

      {active && (
        <LeadDrawer
          lead={active}
          statuses={statuses}
          onClose={() => setActive(null)}
          onChanged={(updated) => {
            setActive(updated);
            load();
          }}
        />
      )}
    </div>
  );
}

function CountChip({ label, value, status, active, onClick, testid }) {
  const c = status ? STATUS_COLORS[status] : null;
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="px-2.5 py-1 rounded transition-colors"
      style={{
        background: active ? "var(--fop-surface-3)" : "var(--fop-surface-2)",
        border: `1px solid ${active ? "var(--fop-primary)" : "var(--fop-border)"}`,
        color: c?.text || "var(--fop-text-2)",
      }}
    >
      {label} · {value}
    </button>
  );
}

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.new;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono uppercase tracking-wider"
      style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text }}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

function LeadRow({ lead, onOpen }) {
  return (
    <button
      onClick={onOpen}
      data-testid={`lead-row-${lead.id}`}
      className="w-full text-left px-5 py-4 hover:bg-[var(--fop-surface-2)] transition-colors"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-semibold text-[var(--fop-text)] truncate">{lead.company || "—"}</span>
            <StatusBadge status={lead.status || "new"} />
            {lead.company_type && (
              <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                {COMPANY_TYPE_LABELS[lead.company_type] || lead.company_type}
              </span>
            )}
          </div>
          <div className="text-sm text-[var(--fop-text-2)] truncate">
            {lead.name} · <span className="text-[var(--fop-primary)]">{lead.email}</span>
            {lead.phone && <span className="text-[var(--fop-text-3)]"> · {lead.phone}</span>}
          </div>
          {lead.message && (
            <div className="text-sm text-[var(--fop-text-3)] mt-1 line-clamp-1">{lead.message}</div>
          )}
        </div>
        <div className="text-right text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] shrink-0">
          <div>{fmtDate(lead.created_at)}</div>
          {lead.assigned_to && (
            <div className="text-[var(--fop-text-2)] mt-1">→ {lead.assigned_to}</div>
          )}
        </div>
      </div>
    </button>
  );
}

function LeadDrawer({ lead, statuses, onClose, onChanged }) {
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const [assigned, setAssigned] = useState(lead.assigned_to || "");
  const [nextAction, setNextAction] = useState(lead.next_action_at?.slice(0, 10) || "");
  const [convertOpen, setConvertOpen] = useState(false);
  const isConverted = Boolean(lead.converted_tenant_id);

  const updateLead = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/marketing/demo-requests/${lead.id}`, patch);
      toast.success("Lead updated");
      onChanged(data.lead);
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed to update lead");
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await api.post(`/marketing/demo-requests/${lead.id}/notes`, { text: noteText.trim() });
      toast.success("Note added");
      setNoteText("");
      // Reload by patching with empty status (no), simpler: refetch entire detail.
      const { data } = await api.get("/marketing/demo-requests", { params: {} });
      const fresh = (data.leads || []).find((l) => l.id === lead.id);
      if (fresh) onChanged(fresh);
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed to add note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[180] flex items-stretch justify-end"
      style={{ background: "rgba(2, 6, 23, 0.65)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="lead-drawer"
    >
      <div
        className="w-full max-w-xl h-full bg-[var(--fop-surface)] border-l border-[var(--fop-border)] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b flex items-start justify-between gap-4"
             style={{ borderColor: "var(--fop-border)" }}>
          <div className="min-w-0">
            <div className="font-display text-xl text-[var(--fop-text)] truncate">{lead.company}</div>
            <div className="text-sm text-[var(--fop-text-2)] mt-0.5 truncate">
              {lead.name} · <a href={`mailto:${lead.email}`} className="text-[var(--fop-primary)] hover:underline">{lead.email}</a>
            </div>
            {lead.phone && (
              <div className="text-sm text-[var(--fop-text-3)] mt-0.5">{lead.phone}</div>
            )}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <StatusBadge status={lead.status || "new"} />
              {lead.company_type && (
                <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                  {COMPANY_TYPE_LABELS[lead.company_type] || lead.company_type}
                </span>
              )}
              {lead.estimated_users && (
                <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                  {lead.estimated_users} users
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
                  data-testid="lead-drawer-close">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {lead.message && (
            <Section title="Message">
              <p className="text-sm text-[var(--fop-text-2)] whitespace-pre-wrap leading-relaxed">{lead.message}</p>
            </Section>
          )}

          {/* Convert to Customer — only visible to platform admins (the entire
              drawer is platform-admin gated, so visibility here just depends
              on whether the lead has already been converted). */}
          {isConverted ? (
            <ConvertedBlock lead={lead} />
          ) : (
            <Section title="Convert">
              <div className="rounded-lg p-4"
                   style={{
                     background: "linear-gradient(180deg, rgba(249,115,22,0.08), rgba(249,115,22,0.02))",
                     border: "1px solid rgba(249,115,22,0.35)",
                   }}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-[var(--fop-text)] text-base mb-1">
                      Convert to customer
                    </div>
                    <div className="text-sm text-[var(--fop-text-2)]">
                      Create a customer tenant for <span className="text-[var(--fop-text)] font-medium">{lead.company}</span> and send the initial owner invite.
                    </div>
                  </div>
                  <button
                    onClick={() => setConvertOpen(true)}
                    disabled={saving}
                    className="btn btn-primary"
                    style={{ minHeight: "40px", padding: "0 16px" }}
                    data-testid="lead-convert-open"
                  >
                    <Building2 size={14} /> Convert to Customer
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </Section>
          )}

          <Section title="Status">
            <div className="flex flex-wrap gap-2">
              {Object.entries(statuses).map(([code, label]) => (
                <button
                  key={code}
                  disabled={saving}
                  onClick={() => updateLead({ status: code })}
                  data-testid={`lead-status-${code}`}
                  className="px-2.5 py-1 rounded text-[11px] font-mono uppercase tracking-wider"
                  style={{
                    background: lead.status === code ? STATUS_COLORS[code]?.bg : "var(--fop-surface-2)",
                    border: `1px solid ${lead.status === code ? STATUS_COLORS[code]?.border : "var(--fop-border)"}`,
                    color: lead.status === code ? STATUS_COLORS[code]?.text : "var(--fop-text-2)",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Assignment & next action">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Assigned to</Label>
                <input
                  value={assigned}
                  onChange={(e) => setAssigned(e.target.value)}
                  placeholder="e.g. Mike"
                  data-testid="lead-assigned"
                />
              </div>
              <div>
                <Label>Next action date</Label>
                <input
                  type="date"
                  value={nextAction}
                  onChange={(e) => setNextAction(e.target.value)}
                  data-testid="lead-next-action"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                disabled={saving}
                onClick={() => updateLead({ assigned_to: assigned, next_action_at: nextAction || null })}
                className="btn btn-primary"
                style={{ minHeight: "40px", padding: "0 14px" }}
                data-testid="lead-save"
              >
                <UserCheck size={14} /> Save
              </button>
              <button
                disabled={saving}
                onClick={() => updateLead({ mark_contacted: true })}
                className="btn btn-ghost"
                style={{ minHeight: "40px", padding: "0 14px" }}
                data-testid="lead-mark-contacted"
              >
                <CheckCircle2 size={14} /> Mark contacted
              </button>
              <button
                disabled={saving}
                onClick={() => updateLead({ status: "archived" })}
                className="btn btn-ghost"
                style={{ minHeight: "40px", padding: "0 14px" }}
                data-testid="lead-archive"
              >
                Archive
              </button>
            </div>
            <div className="mt-2 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] space-y-0.5">
              <div>Last contacted: {fmtDate(lead.last_contacted_at)}</div>
              <div>Created: {fmtDate(lead.created_at)}</div>
              {lead.source && <div>Source: {lead.source}</div>}
            </div>
          </Section>

          <Section title="Internal notes">
            <div className="space-y-2 mb-3">
              {(lead.notes || []).length === 0 ? (
                <div className="text-sm text-[var(--fop-text-3)]">No notes yet.</div>
              ) : (
                (lead.notes || []).map((n) => (
                  <div key={n.id}
                       className="rounded-md p-3 text-sm"
                       style={{
                         background: "var(--fop-surface-2)",
                         border: "1px solid var(--fop-border)",
                       }}>
                    <div className="text-[var(--fop-text)] whitespace-pre-wrap">{n.text}</div>
                    <div className="mt-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                      {n.author_email || n.author_name || "—"} · {fmtDate(n.at)}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div>
              <textarea
                rows={3}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Internal note — visible only to ForgedOps staff"
                data-testid="lead-note-text"
                style={{ minHeight: "82px", resize: "vertical" }}
              />
              <button
                disabled={saving || !noteText.trim()}
                onClick={addNote}
                className="btn btn-primary mt-2"
                style={{ minHeight: "40px", padding: "0 14px" }}
                data-testid="lead-add-note"
              >
                <Send size={14} /> Add note
              </button>
            </div>
          </Section>

          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-4)] pt-4 border-t"
               style={{ borderColor: "var(--fop-border)" }}>
            Lead ID: {lead.id}
          </div>
        </div>
      </div>

      {convertOpen && (
        <ConvertLeadModal
          lead={lead}
          onClose={() => setConvertOpen(false)}
          onConverted={(fresh) => {
            setConvertOpen(false);
            onChanged(fresh);
          }}
        />
      )}
    </div>
  );
}

function ConvertedBlock({ lead }) {
  const copyInvite = async () => {
    try {
      const base = window.location.origin;
      const url = `${base}${lead.converted_invite_url || ""}`;
      // We don't always have the invite URL fresh — fall back to a notice if missing.
      if (!lead.converted_invite_url) {
        toast.info("Open the new tenant in the Operations Console to re-issue the invite if needed.");
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("Invite link copied");
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <Section title="Converted">
      <div
        className="rounded-lg p-4"
        style={{
          background: "linear-gradient(180deg, rgba(34,197,94,0.07), rgba(34,197,94,0.02))",
          border: "1px solid rgba(34,197,94,0.35)",
        }}
        data-testid="lead-converted-block"
      >
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 size={16} className="text-[var(--fop-success)]" />
          <div className="font-display text-[var(--fop-text)]">Customer tenant created</div>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-5 gap-y-2 text-sm">
          <KV k="Tenant"        v={lead.converted_tenant_slug || "—"} />
          <KV k="Owner email"   v={lead.converted_owner_email || "—"} />
          <KV k="Converted at"  v={fmtDate(lead.converted_at)} />
          <KV k="Converted by"  v={lead.converted_by || "—"} />
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {lead.converted_invite_url && (
            <button
              onClick={copyInvite}
              className="btn btn-ghost"
              style={{ minHeight: "36px", padding: "0 12px" }}
              data-testid="lead-copy-invite"
            >
              <Copy size={14} /> Copy Invite Link
            </button>
          )}
          <a
            href={`/platform`}
            className="btn btn-ghost"
            style={{ minHeight: "36px", padding: "0 12px" }}
            data-testid="lead-open-tenant"
          >
            <ExternalLink size={14} /> Open in Operations Console
          </a>
        </div>
      </div>
    </Section>
  );
}

function KV({ k, v }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">{k}</div>
      <div className="text-[var(--fop-text)] truncate">{v}</div>
    </div>
  );
}

function ConvertLeadModal({ lead, onClose, onConverted }) {
  const initialSlug = slugify(lead.company || "");
  const [companyName, setCompanyName]   = useState(lead.company || "");
  const [companySlug, setCompanySlug]   = useState(initialSlug);
  const [ownerName, setOwnerName]       = useState(lead.name || "");
  const [ownerEmail, setOwnerEmail]     = useState(lead.email || "");
  const [primary, setPrimary]           = useState("#F97316");
  const [secondary, setSecondary]       = useState("#111827");
  const [note, setNote]                 = useState("");
  const [slugDirty, setSlugDirty]       = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [result, setResult]             = useState(null); // { tenant_id, owner_invite_url, ... }
  const [error, setError]               = useState("");

  const onCompanyChange = (v) => {
    setCompanyName(v);
    if (!slugDirty) setCompanySlug(slugify(v));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/marketing/demo-requests/${lead.id}/convert`,
        {
          company_name: companyName.trim(),
          company_slug: companySlug.trim().toLowerCase(),
          initial_owner_name: ownerName.trim(),
          initial_owner_email: ownerEmail.trim().toLowerCase(),
          primary_color: primary || null,
          secondary_color: secondary || null,
          note: note.trim() || null,
        },
      );
      setResult(data);
      toast.success("Lead converted to customer");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) {
        const first = detail[0];
        const path = (first?.loc || []).slice(1).join(".");
        msg = `${path}: ${first?.msg || "invalid"}`;
      } else if (typeof detail === "string") {
        msg = detail;
      } else {
        msg = `Conversion failed (${status || "unknown"})`;
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const closeAndPropagate = async () => {
    if (result?.lead) {
      // Decorate the lead with the invite URL so the drawer's Converted block
      // can offer a Copy Invite Link button.
      onConverted({
        ...result.lead,
        converted_invite_url: result.owner_invite_url,
      });
    } else {
      onClose();
    }
  };

  const copyInvite = async () => {
    if (!result?.owner_invite_url) return;
    try {
      await navigator.clipboard.writeText(window.location.origin + result.owner_invite_url);
      toast.success("Invite link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: "rgba(2,6,23,0.78)", backdropFilter: "blur(8px)" }}
      onClick={result ? closeAndPropagate : onClose}
      data-testid="convert-modal"
    >
      <div
        className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-xl"
        style={{
          background: "linear-gradient(180deg, #111827 0%, #0a0f1c 100%)",
          border: "1px solid var(--fop-border-2)",
          boxShadow: "0 30px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(249,115,22,0.06) inset",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={result ? closeAndPropagate : onClose}
          disabled={submitting}
          className="absolute top-4 right-4 text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
          data-testid="convert-modal-close"
        >
          <X size={20} />
        </button>

        <div className="px-7 pt-8 pb-7">
          {result ? (
            <div className="text-center py-3" data-testid="convert-success">
              <div className="inline-flex w-14 h-14 items-center justify-center rounded-full mb-4"
                   style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.4)" }}>
                <CheckCircle2 size={28} className="text-[var(--fop-success)]" />
              </div>
              <h2 className="font-display text-2xl text-[var(--fop-text)] mb-1">Customer created</h2>
              <p className="text-sm text-[var(--fop-text-2)] mb-5">
                {result.tenant_display_name} · <span className="text-[var(--fop-primary)]">{result.tenant_slug}</span>
              </p>
              <div
                className="text-left rounded-md p-3 mb-4 text-xs font-mono break-all"
                style={{
                  background: "var(--fop-surface-2)",
                  border: "1px solid var(--fop-border)",
                  color: "var(--fop-text-2)",
                }}
              >
                {window.location.origin}{result.owner_invite_url}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <button onClick={copyInvite} className="btn btn-primary"
                        style={{ minHeight: "44px", padding: "0 18px" }}
                        data-testid="convert-copy-invite">
                  <Copy size={14} /> Copy Invite Link
                </button>
                <button onClick={closeAndPropagate} className="btn btn-ghost"
                        style={{ minHeight: "44px", padding: "0 18px" }}
                        data-testid="convert-done">
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--fop-primary)] mb-2">
                Convert Lead
              </div>
              <h2 className="font-display text-2xl text-[var(--fop-text)] mb-1">
                Create customer tenant
              </h2>
              <p className="text-sm text-[var(--fop-text-2)] mb-5">
                Spin up the tenant and send the initial owner invite. The lead
                is linked to the new tenant.
              </p>

              <form onSubmit={submit} className="space-y-4" data-testid="convert-form">
                <Field label="Customer display name" required>
                  <input value={companyName} onChange={(e) => onCompanyChange(e.target.value)}
                         required maxLength={120}
                         data-testid="convert-company-name" />
                </Field>
                <Field label="Tenant slug" required hint="lowercase a-z, 0-9, dashes">
                  <input value={companySlug}
                         onChange={(e) => { setSlugDirty(true); setCompanySlug(e.target.value); }}
                         required maxLength={42}
                         pattern="^[a-z0-9][a-z0-9-]{1,40}[a-z0-9]$"
                         data-testid="convert-slug" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Initial owner name" required>
                    <input value={ownerName} onChange={(e) => setOwnerName(e.target.value)}
                           required maxLength={120}
                           data-testid="convert-owner-name" />
                  </Field>
                  <Field label="Initial owner email" required>
                    <input type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)}
                           required
                           data-testid="convert-owner-email" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Brand primary">
                    <input type="text" value={primary} onChange={(e) => setPrimary(e.target.value)}
                           placeholder="#F97316"
                           data-testid="convert-primary-color" />
                  </Field>
                  <Field label="Brand secondary">
                    <input type="text" value={secondary} onChange={(e) => setSecondary(e.target.value)}
                           placeholder="#111827"
                           data-testid="convert-secondary-color" />
                  </Field>
                </div>
                <Field label="Conversion note (optional)">
                  <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                            placeholder="Operator notes — attached to the lead as an internal note"
                            data-testid="convert-note"
                            style={{ minHeight: "70px", resize: "vertical" }} />
                </Field>

                {error && (
                  <div
                    className="text-sm font-mono px-3 py-2 rounded"
                    style={{
                      background: "rgba(220,38,38,0.08)",
                      border: "1px solid rgba(220,38,38,0.35)",
                      color: "#fca5a5",
                    }}
                    data-testid="convert-error"
                  >
                    {error}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <button type="submit" disabled={submitting}
                          className="btn btn-primary flex-1"
                          style={{ minHeight: "44px" }}
                          data-testid="convert-submit">
                    {submitting ? <><Loader2 size={14} className="animate-spin" /> Converting…</> : <>Create customer <ArrowRight size={14} /></>}
                  </button>
                  <button type="button" onClick={onClose} disabled={submitting}
                          className="btn btn-ghost sm:w-32"
                          style={{ minHeight: "44px" }}
                          data-testid="convert-cancel">
                    Cancel
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs font-mono uppercase tracking-wider text-[var(--fop-text-2)] mb-1.5 flex items-baseline gap-2">
        <span>{label}{required && <span className="text-[var(--fop-primary)] ml-1">*</span>}</span>
        {hint && <span className="text-[10px] text-[var(--fop-text-4)] normal-case tracking-normal">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function slugify(name) {
  return (name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 42);
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--fop-text-3)] mb-2.5">{title}</div>
      {children}
    </div>
  );
}
function Label({ children }) {
  return (
    <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] mb-1">{children}</div>
  );
}
